'use client';

import React, { useCallback } from 'react';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { alpha, Box, IconButton, InputBase, SxProps, Theme } from '@mui/material';

import { useT } from '@/hooks';

interface ExpandableSearchProps {
  /** The current search query value */
  value: string;
  /** Callback when the search query changes */
  onChange: (query: string) => void;
  /** Whether the search bar is currently expanded */
  isOpen: boolean;
  /** Callback to toggle the expanded state */
  onToggle: (isOpen: boolean) => void;
  /** Placeholder text for the input field */
  placeholder?: string;
  /** Optional custom width for the expanded state */
  expandedWidth?: string | number | object;
  /** Optional custom styles for the container */
  sx?: SxProps<Theme>;
}

/**
 * A reusable, expandable search component that transitions from a simple icon
 * to a full-width input field.
 */
export const ExpandableSearch = ({
  value,
  onChange,
  isOpen,
  onToggle,
  placeholder,
  expandedWidth = { xs: '100%', sm: 260 },
  sx = {},
}: ExpandableSearchProps) => {
  const { t } = useT();
  const actualPlaceholder = placeholder || t('common.search', { defaultValue: 'Search' });

  const handleOpen = useCallback(() => {
    onToggle(true);
  }, [onToggle]);

  const handleClose = useCallback(() => {
    onToggle(false);
    onChange('');
  }, [onToggle, onChange]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  if (isOpen) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.05),
          borderRadius: '8px',
          px: 1.5,
          py: 0.5,
          width: expandedWidth,
          border: '1px solid',
          borderColor: 'divider',
          ...sx,
        }}
      >
        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 1 }} />
        <InputBase
          autoFocus
          placeholder={actualPlaceholder}
          value={value}
          onChange={handleChange}
          sx={{
            flex: 1,
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'text.primary',
            '& input::placeholder': {
              color: (theme) => alpha(theme.palette.text.primary, 0.4),
              opacity: 1,
            },
          }}
        />
        <IconButton size="small" onClick={handleClose} sx={{ p: 0.5, color: 'text.secondary' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <IconButton
      onClick={handleOpen}
      sx={{
        color: 'text.secondary',
        '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
        ...sx,
      }}
    >
      <SearchIcon fill="currentColor" />
    </IconButton>
  );
};
